import { Entity, EntityType } from '~/types/Entity';
import { captureException } from '~/utils/sentry';

import { api } from '../createApi';
import { TagType } from '../TagType';

const CONVERSATION_MESSAGES_TAG = {
  type: TagType.ConversationalAi,
  id: 'MESSAGES',
};

// Store pending conversations that need to be injected
const pendingConversations = new Map<string, any>();

export const clearPendingConversations = () => {
  pendingConversations.clear();
};

api.enhanceEndpoints({
  addTagTypes: [TagType.ConversationalAi as TagType],
  endpoints: {
    getConversations: {
      providesTags: (_result, _error, arg: Entity<EntityType>) => [
        {
          type: TagType.ConversationalAi,
          id: arg
            ? `CONVERSATION-${arg.type}-${arg.id}`
            : 'CONVERSATION-UNKNOWN',
        },
      ],
      async onQueryStarted(
        entity: Entity<EntityType>,
        { dispatch, queryFulfilled },
      ) {
        try {
          // Skip if entity is invalid
          if (!entity || !entity.type || !entity.id) {
            return;
          }

          const { data } = await queryFulfilled;
          const cacheKey = `${entity.type}-${entity.id}`;
          const pendingConversation = pendingConversations.get(cacheKey);

          // If there's a pending conversation for this entity, inject it
          if (pendingConversation && data) {
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                entity,
                (draft: any) => {
                  if (draft && draft.items) {
                    // Check if conversation already exists (backend caught up)
                    const exists = draft.items.some(
                      (item: any) => item.id === pendingConversation.id,
                    );
                    if (!exists) {
                      draft.items.unshift(pendingConversation);
                      draft.total += 1;
                    } else {
                      // Backend has caught up, clear the pending conversation
                      pendingConversations.delete(cacheKey);
                    }
                  }
                },
              ),
            );
          }
        } catch (e) {
          captureException('Failed to update conversations with pending', e, {
            section: 'conversationalAi getConversations onQueryStarted',
          });
          clearPendingConversations();
        }
      },
    },
    getConversation: {
      providesTags: [CONVERSATION_MESSAGES_TAG],
    },
    createConversation: {
      async onQueryStarted(
        entity: Entity<EntityType>,
        { dispatch, queryFulfilled },
      ) {
        // Skip if entity is invalid
        if (!entity || !entity.type || !entity.id) {
          return;
        }

        const { id: entityId, type: entityType } = entity;
        const cacheKey = `${entityType}-${entityId}`;

        try {
          const { data: newConversation } = await queryFulfilled;
          if (newConversation) {
            // Store the conversation as pending for this entity
            pendingConversations.set(cacheKey, newConversation);

            // Try to update the cache if it exists
            dispatch(
              api.util.updateQueryData(
                'getConversations',
                entity,
                (draft: any) => {
                  if (draft && draft.items) {
                    draft.items.unshift(newConversation);
                    draft.total += 1;
                  }
                },
              ),
            );
          }
        } catch {
          // On error, invalidate the specific entity conversation tag
          dispatch(
            api.util.invalidateTags([
              {
                type: TagType.ConversationalAi,
                id: `CONVERSATION-${entityType}-${entityId}`,
              },
            ]),
          );
        }
      },
    },
    postUserMessage: {
      invalidatesTags: [CONVERSATION_MESSAGES_TAG],
    },
  },
});
